import { useMutation, useQueryClient } from "@tanstack/react-query";

import { useDomainServices } from "@/providers/DomainServices";

import { RobotoLLMContext } from "../ChatRecord";

interface SendMessageParams {
  chatId: string;
  message: string;
  orgId: string;
  context?: RobotoLLMContext;
}

/**
 * Hook to send a new message to an existing chat conversation.
 *
 * After successfully sending a message, this hook will immediately refetch the chat
 * query to show the new message and start polling for the assistant response.
 *
 * @returns React Query mutation for sending messages
 */
export function useSendMessage() {
  const { chatService } = useDomainServices();
  const queryClient = useQueryClient();

  return useMutation<void, Error, SendMessageParams>({
    mutationFn: async ({ chatId, message, orgId, context }) => {
      return chatService.sendMessage(chatId, message, { orgId, context });
    },
    onSuccess: async (_, variables) => {
      // Immediately refetch the chat query to show the new message and start polling
      return queryClient.refetchQueries({
        queryKey: ["chat", variables.chatId],
      });
    },
  });
}
